import { useEffect, useState, useRef } from 'react';
import Text from '../../../../components/Text';
import * as Flags from 'country-flag-icons/react/3x2';
import { fontSizes } from '../../../../constants/constants';
import { countries_length } from '../../../../config/config';
import { isValidPhoneNumber } from 'libphonenumber-js';
import dynamicfunction from '../../../../utils/dynamicfunction';
import { cn, getContentKeyAccToLang } from '../../../../utils/utils';
import RowView from '../RowView';
import { useSelector } from 'react-redux';

const PhoneInput = ({
  label,
  value,
  isError,
  isRequired = false,
  onChange,
  isDisabled,
  error,
  labelDivClass,
  isLabelHeight = false,
  labelColor
}) => {
  const [country, setCountry] = useState(countries_length[191]);
  const [phone, setPhone] = useState('');
  const [isValid, setIsValid] = useState(null);
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const dropdownRef = useRef(null);
  const searchRef = useRef(null);

  const { isArabic, TextWeight } = dynamicfunction();
 const { dashboardContent } = useSelector((state) => state.content);

  const getPlaceholderKey = (key, isColon = false) => {
    return getContentKeyAccToLang(dashboardContent, key, isArabic, isColon);
  };

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false);
        setSearch('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Focus search input when dropdown opens
  useEffect(() => {
    if (isOpen && searchRef.current) {
      setTimeout(() => searchRef.current?.focus(), 50);
    }
  }, [isOpen]);

  useEffect(() => {
    if (!value) {
      setPhone('');
      setIsValid(null);
      return;
    }

    const strValue = value.toString();
    const sortedCountries = [...countries_length].sort(
      (a, b) => b.phone.toString().length - a.phone.toString().length,
    );
    const matchedCountry = sortedCountries.find((c) =>
      strValue.startsWith(`+${c.phone}`),
    );

    if (matchedCountry) {
      setCountry(matchedCountry);
      const nationalNumber = strValue
        .slice(`+${matchedCountry.phone}`.length)
        .replace(/\D/g, '');
      setPhone(nationalNumber);
    }
  }, [value]);

  const handleChange = (rawDigits, selectedCountry = country) => {
    const maxDigits = selectedCountry?.phoneLength ?? 15;
    const digits = rawDigits.replace(/\D/g, '').substring(0, maxDigits);
    const fullNumber = `+${selectedCountry?.phone}${digits}`;

    setPhone(digits);

    if (digits.length === 0) {
      setIsValid(null);
      onChange('', false);
      return;
    }

    try {
      const valid = isValidPhoneNumber(fullNumber, selectedCountry?.code);
      setIsValid(valid);
      onChange(fullNumber, valid);
    } catch {
      setIsValid(false);
      onChange(fullNumber, false);
    }
  };

  const handleCountryChange = (val) => {
    setCountry(val);
    setPhone('');
    setIsValid(null);
    setIsOpen(false);
    setSearch('');
    onChange(`+${val?.phone}`, false);
  };

  const code = country?.code ?? 'SA';
  const Flag = Flags[code] ?? Flags['SA'];

  const maxLength = country?.phoneLength
    ? country.phoneLength + Math.ceil(country.phoneLength / 3) - 1
    : 15;

  const errorMessage =
    typeof isError === 'string'
      ? isError
      : isValid === false
        ? error
        : null;

  const filteredCountries = countries_length.filter((c) => {
    const q = search.toLowerCase();
    return (
      c.label?.toLowerCase().includes(q) ||
      c.code?.toLowerCase().includes(q) ||
      c.phone?.toString().includes(q)
    );
  });

  return (
    <div className={isDisabled ? 'opacity-50' : ''}>
      {label && (
        <div className={cn(labelDivClass ? labelDivClass : isLabelHeight ? 'md:h-9' : "h-6")} >
          <Text
            text={label}
            size={fontSizes.h14}
            isRequired={isRequired}
            isTruncate={false}
            color={labelColor}
            className={cn('leading-4', isLabelHeight && 'mb-1')}
          />
        </div>
      )}

      {/* dir="ltr" — flag → +code → input always left-to-right regardless of page direction */}
      <div className="w-full flex border rounded-lg pe-1 h-[40px]" dir="ltr">

        {/* Country selector */}
        <div className="relative" ref={dropdownRef}>
          <div
            className="flex flex-row gap-2 items-center h-[38px] px-2 cursor-pointer select-none"
            dir="ltr"
            onClick={() => {
              if (!isDisabled) setIsOpen((prev) => !prev);
            }}
          >
            <Flag title={country?.code} className="h-5 rounded-sm shrink-0" />
            <span className="text-sm" dir="ltr">{`+${country?.phone}`}</span>
          </div>

          {isOpen && (
            // dir="ltr" on dropdown: flag | country name | +code (left to right)
            <div
              className="absolute top-10 left-0 z-20 bg-white border border-border rounded-md shadow-lg w-[300px] max-h-64 flex flex-col"
              dir="ltr"
            >
              {/* Search box */}
              <div className="p-2 border-b border-border sticky top-0 bg-white">
                <RowView className="flex items-center gap-2 border border-border rounded-md px-2 h-8">
                  <svg
                    className="w-3.5 h-3.5 text-gray-400 shrink-0"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M9 3.5a5.5 5.5 0 100 11 5.5 5.5 0 000-11zM2 9a7 7 0 1112.452 4.391l3.328 3.329a.75.75 0 11-1.06 1.06l-3.329-3.328A7 7 0 012 9z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <input
                    ref={searchRef}
                    type="text"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder={getPlaceholderKey('search_country')}
                    className={`outline-none text-sm flex-1 bg-transparent ${isArabic ? 'text-right' : 'text-left'}`}
                    onClick={(e) => e.stopPropagation()}
                    dir="auto"
                  />
                  {search && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setSearch('');
                      }}
                      className="text-gray-400 hover:text-gray-600"
                    >
                      ×
                    </button>
                  )}
                </RowView>
              </div>

              {/* Country list */}
              <div className="overflow-y-auto flex-1">
                {filteredCountries.length === 0 ? (
                  <div className="py-4 px-4 text-sm text-gray-400 text-center">
                    {getPlaceholderKey('no_countries_found')}
                  </div>
                ) : (
                  filteredCountries.map((item, index) => {
                    const ItemFlag = Flags[item?.code];
                    const isSelected = item?.code === country?.code;
                    return (
                      <div
                        key={index}
                        className={cn(
                          'py-2 px-4 flex items-center gap-2 border-b border-border cursor-pointer hover:bg-gray-50',
                          isSelected && 'bg-primary/10',
                        )}
                        onClick={() => handleCountryChange(item)}
                      >
                        {ItemFlag && (
                          <ItemFlag title={item?.code} className="h-4 rounded-sm shrink-0" />
                        )}
                        <Text
                          text={item?.label}
                          size={fontSizes.h14}
                          className="block flex-1"
                          isTruncate={false}
                        />
                        {/* dial code — always LTR, pinned right */}
                        <span className="text-sm text-gray-400 shrink-0" dir="ltr">
                          +{item?.phone}
                        </span>
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          )}
        </div>

        {/* Phone number input — always LTR so numbers don't flip */}
        <input
          type="tel"
          value={formatPhone(phone, country?.phoneLength)}
          disabled={isDisabled}
          onChange={(e) => {
            if (isDisabled) return;
            const digits = e.target.value.replace(/\D/g, '');
            handleChange(digits);
          }}
          className="outline-none w-full"
          placeholder="000 000 0000"
          maxLength={maxLength}
          dir="ltr"
        />
      </div>

      {errorMessage && (
        <Text
          text={errorMessage}
          color="text-red"
          className="block mt-2"
          weight={TextWeight.light}
          size={fontSizes.h14}
        />
      )}
    </div>
  );
};

export default PhoneInput;

export const formatPhone = (value, phoneLength) => {
  if (!value) return '';
  const raw = value.replace(/\D/g, '');
  const limit = phoneLength ?? 10;
  const trimmed = raw.substring(0, limit);
  if (trimmed.length <= 3) return trimmed;
  const first = trimmed.substring(0, 3);
  const second = trimmed.substring(3, 6);
  const third = trimmed.substring(6);
  let formatted = first;
  if (second) formatted += ' ' + second;
  if (third) formatted += ' ' + third;
  return formatted;
};
